import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Button, Dialog, useDialogStore } from '@ariakit/react';
import { Filter, Trash2 } from 'lucide-react';
import type { Table, Column, ColumnFilter } from '@tanstack/react-table';
import { DataTableColumn, FilterStyle, CompoundFilterValue } from '../types';
import ColumnFilterPanel, {
  ColumnFilterPanelHandle,
  ColumnFilterPanelState,
  buildFilterValue,
  computeDistinctValues,
  isRuleActive,
  readFilterStateFromColumn,
} from './ColumnFilterPanel';

interface MultiFilterProps<TData> {
  table: Table<TData>;
  filterStyle?: FilterStyle;
}

/**
 * Returns true if the given filter value represents an active (non-trivial) filter.
 */
function isColumnFilterActive(filterValue: unknown): boolean {
  if (filterValue === undefined || filterValue === null) return false;
  if (Array.isArray(filterValue)) return true; // string[] selections
  if (typeof filterValue === 'object') {
    const compound = filterValue as CompoundFilterValue;
    const hasSelectedValues = compound.selectedValues !== undefined;
    const hasAdvancedRules = (compound.advancedRules || []).filter(isRuleActive).length > 0;
    return hasSelectedValues || hasAdvancedRules;
  }
  return false;
}

export default function MultiFilter<TData>({ table, filterStyle = 'select' }: MultiFilterProps<TData>) {
  const [isOpen, setIsOpen] = useState(false);
  const dialog = useDialogStore({ open: isOpen, setOpen: setIsOpen });
  const [columnSearchQuery, setColumnSearchQuery] = useState('');
  const [selectedColumnId, setSelectedColumnId] = useState<string | null>(null);

  // Store pending filter states per column (keyed by column id).
  // These represent uncommitted edits inside the dialog.
  const [pendingStates, setPendingStates] = useState<Record<string, ColumnFilterPanelState>>({});

  // Key to force re-mount the panel when switching columns
  const [panelKey, setPanelKey] = useState(0);
  const panelRef = useRef<ColumnFilterPanelHandle>(null);

  // Ref to track the currently selected column id so callbacks always
  // read the latest value without stale closures.
  const selectedColumnIdRef = useRef<string | null>(selectedColumnId);

  // Mirror pendingStates into a ref so handleApply always reads the
  // latest value, avoiding the stale closure problem.
  const pendingStatesRef = useRef<Record<string, ColumnFilterPanelState>>(pendingStates);
  pendingStatesRef.current = pendingStates;


  // All columns that can be filtered
  const filterableColumns = useMemo(() => {
    return table.getAllColumns().filter(col => {
      const colDef = col.columnDef as DataTableColumn<TData>;
      return col.getCanFilter() && colDef.enableFiltering !== false;
    });
  }, [table]);

  // Columns matching the search in the left sidebar
  const filteredColumns = useMemo(() => {
    if (!columnSearchQuery) return filterableColumns;
    const search = columnSearchQuery.toLowerCase();
    return filterableColumns.filter(col => {
      const header = col.columnDef.header;
      const name = typeof header === 'string' ? header : col.id;
      return name.toLowerCase().includes(search);
    });
  }, [filterableColumns, columnSearchQuery]);

  // Count of columns that currently have active filters (committed)
  const activeFilterCount = useMemo(() => {
    return filterableColumns.filter(col => col.getIsFiltered()).length;
  }, [filterableColumns, table.getState().columnFilters]);

  // Determine if a column has a pending (uncommitted) edit that differs from no-filter.
  // "All values selected" with no search and no advanced rules is NOT a real filter.
  const hasPendingFilter = useCallback((columnId: string): boolean => {
    const pending = pendingStates[columnId];
    if (!pending) return false;

    const col = table.getColumn(columnId);
    if (!col) return false;

    const colDef = col.columnDef as DataTableColumn<unknown>;
    const hasAdvancedFilters = !!colDef.columnFilterType;
    const distinctValues = computeDistinctValues(col, table, filterStyle);

    const filterValue = buildFilterValue(
      pending.selectedValues,
      pending.advancedRules,
      distinctValues,
      hasAdvancedFilters,
      filterStyle,
      pending.searchValue,
    );

    if (filterValue === undefined) return false;

    // An array containing all distinct values is effectively "no filter"
    if (Array.isArray(filterValue) && filterValue.length === distinctValues.length) {
      return false;
    }

    return true;
  }, [pendingStates, table, filterStyle]);

  // Check if a column will have an active filter after applying
  const willHaveFilter = useCallback((columnId: string): boolean => {
    // If we have a pending edit, check that
    if (pendingStates[columnId] !== undefined) {
      return hasPendingFilter(columnId);
    }
    // Otherwise check the committed filter
    const col = table.getColumn(columnId);
    return col ? col.getIsFiltered() : false;
  }, [pendingStates, hasPendingFilter, table]);

  // Initialize dialog state when opened
  useEffect(() => {
    if (isOpen) {
      // Reset pending states — start fresh from committed column filters
      setPendingStates({});
      pendingStatesRef.current = {};
      setColumnSearchQuery('');

      // Auto-select the first filterable column
      if (filterableColumns.length > 0 && !selectedColumnId) {
        const firstId = filterableColumns[0].id;
        selectedColumnIdRef.current = firstId;
        setSelectedColumnId(firstId);
      }
      setPanelKey(prev => prev + 1);
    }
  }, [isOpen]);

  // Snapshot the current panel state before switching away
  const snapshotCurrentPanel = useCallback(() => {
    if (selectedColumnId && panelRef.current) {
      const state = panelRef.current.getState();
      setPendingStates(prev => {
        const next = { ...prev, [selectedColumnId]: state };
        pendingStatesRef.current = next;
        return next;
      });
    }
  }, [selectedColumnId]);

  const handleSelectColumn = useCallback((columnId: string) => {
    if (columnId === selectedColumnId) return;
    // Save current panel state
    snapshotCurrentPanel();
    // Update the ref BEFORE setting state so that when the new panel
    // mounts and fires onStateChange, the callback reads the correct column id.
    selectedColumnIdRef.current = columnId;
    setSelectedColumnId(columnId);
    setPanelKey(prev => prev + 1);
  }, [selectedColumnId, snapshotCurrentPanel]);

  // When the panel state changes, update pending states.
  // Uses selectedColumnIdRef to avoid stale closure issues when the panel
  // remounts after a column switch and fires onStateChange before the
  // parent re-renders with the new selectedColumnId.
  // Also updates pendingStatesRef directly so handleApply always reads
  // the latest value without waiting for a re-render.
  const handlePanelStateChange = useCallback((state: ColumnFilterPanelState) => {
    const currentColumnId = selectedColumnIdRef.current;
    if (!currentColumnId) return;
    setPendingStates(prev => {
      const next = { ...prev, [currentColumnId]: state };
      pendingStatesRef.current = next;
      return next;
    });
  }, []);

  const handleApply = () => {
    // Read the latest pendingStates from the ref to avoid stale closure issues.
    const finalStates = { ...pendingStatesRef.current };

    // For the currently visible panel, read its state directly from the ref
    // to capture any edits not yet flushed via onStateChange/setPendingStates.
    if (selectedColumnId && panelRef.current) {
      finalStates[selectedColumnId] = panelRef.current.getState();
    }

    // Build the complete columnFilters array in one pass, then apply it
    // in a single table.setColumnFilters() call. Calling col.setFilterValue()
    // in a loop causes React batching issues where each call's updater
    // reads stale state, so only the last one survives.
    const existingFilters = table.getState().columnFilters;
    // Start with filters for columns we did NOT touch in the dialog
    const touchedColumnIds = new Set(Object.keys(finalStates));
    const newFilters: ColumnFilter[] = existingFilters.filter(
      (f) => !touchedColumnIds.has(f.id),
    );

    for (const columnId of Object.keys(finalStates)) {
      const col = table.getColumn(columnId);
      if (!col) continue;

      const state = finalStates[columnId];
      const colDef = col.columnDef as DataTableColumn<unknown>;
      const hasAdvancedFilters = !!colDef.columnFilterType;
      const distinctValues = computeDistinctValues(col, table, filterStyle);

      const filterValue = buildFilterValue(
        state.selectedValues,
        state.advancedRules,
        distinctValues,
        hasAdvancedFilters,
        filterStyle,
        state.searchValue,
      );

      // If the filter value is an array containing all distinct values,
      // that's effectively "no filter" — skip it instead of committing.
      if (filterValue === undefined || (Array.isArray(filterValue) && filterValue.length === distinctValues.length)) {
        continue;
      }

      newFilters.push({ id: columnId, value: filterValue });
    }

    table.setColumnFilters(newFilters);
    dialog.hide();
  };

  const handleCancel = () => {
    setPendingStates({});
    pendingStatesRef.current = {};
    dialog.hide();
  };

  const handleClearAll = () => {
    // Clear all column filters in a single operation
    table.setColumnFilters([]);
    setPendingStates({});
    pendingStatesRef.current = {};
    dialog.hide();
  };

  const handleClearCurrentColumn = () => {
    if (!selectedColumnId) return;
    
    // Clear the pending state for this column
    setPendingStates(prev => {
      const newStates = { ...prev };
      delete newStates[selectedColumnId];
      pendingStatesRef.current = newStates;
      return newStates;
    });

    // Clear the panel state
    if (panelRef.current) {
      panelRef.current.clearFilter();
    }
  };

  const selectedColumn = selectedColumnId ? table.getColumn(selectedColumnId) : null;

  // Build initial state for the panel from pending state or committed filter
  const panelInitialState = useMemo((): ColumnFilterPanelState | undefined => {
    if (!selectedColumnId || !selectedColumn) return undefined;

    // If we have a pending edit, use it
    const pending = pendingStates[selectedColumnId];
    if (pending) return pending;

    // Otherwise read from the committed column filter
    const colDef = selectedColumn.columnDef as DataTableColumn<unknown>;
    const hasAdvancedFilters = !!colDef.columnFilterType;
    const distinctValues = computeDistinctValues(selectedColumn, table, filterStyle);
    const { selectedValues, advancedRules } = readFilterStateFromColumn(
      selectedColumn,
      distinctValues,
      hasAdvancedFilters,
    );

    return {
      selectedValues,
      advancedRules,
      searchValue: '',
    };
  }, [selectedColumnId, selectedColumn, pendingStates, table, filterStyle]);

  const getColumnName = (col: Column<TData>): string => {
    const header = col.columnDef.header;
    return typeof header === 'string' ? header : col.id;
  };

  return (
    <>
      <Button
        onClick={dialog.show}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        data-testid="multi-filter-button"
      >
        <Filter className="w-4 h-4" />
        Multi-Filter ({activeFilterCount})
      </Button>

      <Dialog
        store={dialog}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        backdrop={
          <div className="fixed inset-0 bg-black/50" />
        }
        data-testid="multi-filter-dialog"
      >
        <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Configure Filters
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Select a column on the left to configure its filter
            </p>
          </div>

          {/* Body: two-column layout */}
          <div className="flex-1 overflow-hidden flex" style={{ minHeight: 0 }}>
            {/* Left sidebar: column list */}
            <div className="w-64 flex-shrink-0 border-r border-gray-200 flex flex-col bg-gray-50">
              <div className="px-4 py-3 border-b border-gray-200">
                <input
                  type="text"
                  placeholder="Search columns..."
                  value={columnSearchQuery}
                  onChange={(e) => setColumnSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex-1 overflow-y-auto py-2">
                {filteredColumns.length === 0 ? (
                  <div className="text-center py-4 text-gray-400 text-sm">
                    {columnSearchQuery ? 'No columns found' : 'No filterable columns'}
                  </div>
                ) : (
                  filteredColumns.map(col => {
                    const isSelected = selectedColumnId === col.id;
                    const hasFilter = willHaveFilter(col.id);
                    const columnName = getColumnName(col);

                    return (
                      <button
                        key={col.id}
                        onClick={() => handleSelectColumn(col.id)}
                        className={`
                          w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left transition-colors
                          ${isSelected
                            ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-600'
                            : 'text-gray-700 hover:bg-gray-100'
                          }
                        `}
                        data-testid={`multi-filter-column-${col.id}`}
                      >
                        <Filter className={`w-3.5 h-3.5 flex-shrink-0 ${hasFilter ? 'text-blue-600' : 'text-gray-300'}`} />
                        <span className="flex-1 truncate">{columnName}</span>
                        {hasFilter && (
                          <span className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* Right panel: filter controls for the selected column */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {selectedColumn ? (
                <>
                  <div className="px-4 py-3 border-b border-gray-200 bg-white flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-900">
                      {getColumnName(selectedColumn)}
                    </h3>
                    <button
                      onClick={handleClearCurrentColumn}
                      className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded border border-red-300"
                      title="Clear this column's filter"
                    >
                      Clear Filter
                    </button>
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <ColumnFilterPanel
                      key={panelKey}
                      ref={panelRef}
                      column={selectedColumn}
                      table={table}
                      filterStyle={filterStyle}
                      initialState={panelInitialState}
                      onStateChange={handlePanelStateChange}
                      autoFocusSearch
                    />
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <Filter className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Select a column to configure its filter</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
            <Button
              onClick={handleClearAll}
              className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              data-testid="multi-filter-clear-all"
            >
              <Trash2 className="w-4 h-4 mr-2 inline" />
              Clear All Filters
            </Button>

            <div className="flex gap-3">
              <Button
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                data-testid="multi-filter-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleApply}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                data-testid="multi-filter-apply"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}
